import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ReplaySlider.module.scss';
import clsx from 'clsx';
import IconLongestRoad from '@static/images/icon_longest_road_highlight.svg';
import IconLargestArmy from '@static/images/icon_largest_army_highlight.svg';
import IconCardMonopoly from '@static/images/card_monopoly.svg';
const replayHighlightTypeToLobbyImage = {
    largestArmy: IconLargestArmy,
    longestRoad: IconLongestRoad,
    monopolyPlayed: IconCardMonopoly
};
const GRID_AREA_OFFSET = 2;
export const ReplaySlider = ({ value, max, highlightData, onChange, shouldShowHighlights })=>{
    const [isDragging, setIsDragging] = useState(false);
    const valueRef = useRef(value);
    const sliderRef = useRef(null);
    const handleChange = (newValue)=>{
        const isNewValueFirstAction = newValue <= -1;
        const isNewValueLastAction = newValue >= max;
        const isNewValueSameValue = newValue === valueRef.current;
        if (isNewValueSameValue) return;
        if (isNewValueFirstAction) onChange(-1);
        if (isNewValueLastAction) onChange(max);
        valueRef.current = newValue;
        onChange(newValue);
    };
    const highlights = useMemo(()=>{
        if (!highlightData) return null;
        const sorted = [
            ...highlightData
        ].sort((a, b)=>a.actionIndex - b.actionIndex);
        return sorted.map((highlight)=>/*#__PURE__*/ React.createElement("div", {
                onClick: ()=>onChange(highlight.actionIndex),
                key: highlight.actionIndex,
                className: styles.replaySliderHighlight,
                style: {
                    gridArea: `2 / ${highlight.actionIndex + GRID_AREA_OFFSET}`
                }
            }, /*#__PURE__*/ React.createElement("img", {
                className: styles.replaySliderHighlightImage,
                src: replayHighlightTypeToLobbyImage[highlight.type]
            })));
    }, [
        highlightData
    ]);
    const handlePointerDown = ()=>{
        setIsDragging(true);
    };
    useEffect(()=>{
        const handlePointerUp = ()=>{
            setIsDragging(false);
        };
        const handlePointerMove = (e)=>{
            var _el_dataset;
            if (!isDragging || !sliderRef.current) return;
            const sliderRect = sliderRef.current.getBoundingClientRect();
            const sliderRailHalfHeight = 6;
            const sliderMidY = sliderRect.top + sliderRailHalfHeight;
            const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            const el = document.elementFromPoint(clientX, sliderMidY);
            const index = el === null || el === void 0 ? void 0 : (_el_dataset = el.dataset) === null || _el_dataset === void 0 ? void 0 : _el_dataset.index;
            const isPointerExceedSliderLeftEdge = clientX <= sliderRect.left;
            const defaultIndex = isPointerExceedSliderLeftEdge ? -1 : max;
            handleChange(Number(index || defaultIndex));
        };
        if (isDragging) {
            window.addEventListener('mousemove', handlePointerMove);
            window.addEventListener('touchmove', handlePointerMove);
            window.addEventListener('mouseup', handlePointerUp);
            window.addEventListener('touchend', handlePointerUp);
        }
        return ()=>{
            window.removeEventListener('mousemove', handlePointerMove);
            window.removeEventListener('touchmove', handlePointerMove);
            window.removeEventListener('mouseup', handlePointerUp);
            window.removeEventListener('touchend', handlePointerUp);
        };
    }, [
        isDragging,
        max
    ]);
    const handleTrackWidth = useMemo(()=>{
        if (value <= -1) return '0';
        if (value >= max) return '100%';
        return '50%';
    }, [
        value
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.sliderContainer, {
            [styles.hightLightMargin]: shouldShowHighlights
        })
    }, /*#__PURE__*/ React.createElement("div", {
        ref: sliderRef,
        className: styles.sliderWrapper,
        style: {
            '--total-actions': max + GRID_AREA_OFFSET
        }
    }, Array.from({
        length: max + GRID_AREA_OFFSET
    }, (_, i)=>/*#__PURE__*/ React.createElement("div", {
            key: i,
            className: styles.sliderGridClickableCell,
            "data-index": i - 1,
            onPointerDown: ()=>{
                setIsDragging(true);
                handleChange(i - 1);
            },
            style: {
                gridArea: `1 / ${i - 1 + GRID_AREA_OFFSET}`
            }
        })), /*#__PURE__*/ React.createElement("div", {
        className: styles.sliderRail
    }), value > -1 && /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.sliderTrack),
        onMouseDown: handlePointerDown,
        onTouchStart: handlePointerDown,
        style: {
            gridArea: `1 / ${value + GRID_AREA_OFFSET}`,
            gridColumn: `1 / ${value + GRID_AREA_OFFSET}`
        }
    }), /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.sliderTrackHandle),
        onMouseDown: handlePointerDown,
        onTouchStart: handlePointerDown,
        style: {
            width: handleTrackWidth,
            gridArea: `1 / ${value + GRID_AREA_OFFSET}`
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.sliderHandle
    })), highlights));
};
